/**
 * The score screen. SPEC.md §7.1, §18.2, §17.3, §17.4.
 *
 * One viewport, no scrolling, and the keypad is most of it. That ordering is the
 * whole layout: the table's line at the top so a glance answers "am I winning",
 * the keys in the middle because reporting a loss is what the phone is being
 * held to do, and the two commit actions at the bottom under the thumb. The
 * keypad is the flexible part — it takes whatever the summary and the actions
 * have not claimed — so a taller phone simply gets bigger keys. §7.1, §18.2, §20.2
 *
 * Everything else is an overlay behind an icon: who is sitting where, and what
 * has happened so far. Neither is something a player looks at while a hand is
 * being played, and both cost the keypad room it needs. §47
 *
 * Mounting useSession here is what proves identity, opens the socket and holds
 * the screen awake; every child reads state from the stores rather than from
 * props, so nothing has to be threaded through. §16.3, §37
 *
 * Lobby and ended are the same shell with a different middle. Both scroll inside
 * themselves; only the score screen is pinned to one viewport. §33, §52
 */

import { useCallback, useEffect, useRef } from 'react';
import { Spinner } from '@/components/ui/Spinner';
import { useSession } from '@/hooks/useSession';
import { useSessionInfo } from '@/stores/session-store';
import { FinalScoreScreen } from '@/features/session/FinalScoreScreen';
import { LobbyScreen } from '@/features/session/LobbyScreen';
import { ReconnectOverlay } from '@/features/session/ReconnectOverlay';
import { ChangeAvatarSheet } from '@/features/session/ChangeAvatarSheet';
import { TopBar } from '@/features/session/TopBar';
import { HistoryDrawer } from '@/features/history/HistoryDrawer';
import { HostControls } from '@/features/host/HostControls';
import { PlayerAdminSheet } from '@/features/host/PlayerAdminSheet';
import { AmountKeypad } from '@/features/scoring/AmountKeypad';
import { PlayersDrawer } from './PlayersDrawer';
import { RoundExplosion } from './RoundExplosion';
import { TableSummary } from './TableSummary';

/**
 * Publishes the top bar's height as --snackbar-top, which is where the snackbar
 * sits: immediately under it, and nowhere else. §12.1, §10.1
 *
 * There are only four strips a floating notice can cover on this screen, and
 * three of them are wrong. Over the keypad it puts an UNDO exactly where the
 * next digit is about to be tapped. Over the top bar it hides the gear, which is
 * the one control a player wants the instant something went wrong. Over the
 * player boxes — where it used to sit, above the entry zone — it hides the four
 * scores the screen exists to show, at exactly the moment somebody has just
 * changed one of them. §10.1, §18.4, §14.1
 *
 * What is left is the balance line at the top of the summary panel: one row,
 * glanceable rather than tappable, and the same height as the notice covering
 * it. So the snackbar is anchored to the bottom edge of the top bar, and the
 * scores stay visible underneath it.
 *
 * It is the top bar that is measured rather than the panel, because the panel's
 * position is what matters and only its own size would be observable. The bar
 * grows a line when the connection is unhappy, and that is the one thing that
 * moves everything below it. §16.2
 *
 * It goes on the document root because the toast host is mounted once for the
 * whole app, which makes it a sibling of this screen rather than a child. §35
 */
function useSnackbarAnchor(): (element: HTMLElement | null) => void {
  const observer = useRef<ResizeObserver | null>(null);

  const publish = useCallback((height: number) => {
    document.documentElement.style.setProperty(
      '--snackbar-top',
      `${String(Math.round(height))}px`,
    );
  }, []);

  const clear = useCallback(() => {
    document.documentElement.style.removeProperty('--snackbar-top');
  }, []);

  const ref = useCallback(
    (element: HTMLElement | null) => {
      observer.current?.disconnect();
      observer.current = null;

      if (!element) {
        clear();
        return;
      }
      publish(element.getBoundingClientRect().height);

      if (typeof ResizeObserver !== 'function') return;
      const next = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        publish(entry.borderBoxSize?.[0]?.blockSize ?? entry.target.getBoundingClientRect().height);
      });
      next.observe(element);
      observer.current = next;
    },
    [clear, publish],
  );

  useEffect(
    () => () => {
      observer.current?.disconnect();
      observer.current = null;
      clear();
    },
    [clear],
  );

  return ref;
}

/**
 * Sheets and drawers read their own open state from uiStore. §37, §47
 *
 * There is no celebration overlay here any more. A won hand is cheered by the
 * winner's own avatar dancing on its podium in the roster — on the surface the
 * table was already looking at, over nothing, blocking nothing, and with the
 * keypad never so much as tinted. §4.5
 */
function Overlays() {
  return (
    <>
      <PlayersDrawer />
      <PlayerAdminSheet />
      {/* Every player's own, reached from the players overlay. §46, §5.1 */}
      <ChangeAvatarSheet />
      <HistoryDrawer />
      <ReconnectOverlay />
    </>
  );
}

export function GamePage() {
  const { ready } = useSession();
  const session = useSessionInfo();
  const topBarRef = useSnackbarAnchor();

  // Nothing authoritative has arrived yet, so there is nothing honest to draw. §16.3
  if (!session || !ready) {
    return (
      <div className="gameplay-surface relative flex flex-col items-center justify-center gap-3 p-safe">
        <Spinner size="lg" label="Opening your table…" />
        <p className="text-ink-muted">Opening your table…</p>
        <ReconnectOverlay />
      </div>
    );
  }

  if (session.status === 'lobby') {
    return (
      <div className="relative flex min-h-0 flex-1 flex-col">
        <TopBar />
        <LobbyScreen />
        <Overlays />
      </div>
    );
  }

  // An ended game is a read-only summary and is allowed to scroll. §52
  if (session.status === 'ended') {
    return (
      <div className="relative flex min-h-0 flex-1 flex-col">
        <FinalScoreScreen />
        <Overlays />
      </div>
    );
  }

  return (
    <div className="gameplay-surface entry-stack relative flex flex-col px-safe pb-safe">
      {/* Measured, so the snackbar can sit immediately below it. §12.1 */}
      <div ref={topBarRef} className="shrink-0">
        <TopBar />
      </div>

      {/* Takes what the entry zone below did not need, and shrinks rather than
          pushing it. §18.2, §45 */}
      <TableSummary />

      {/*
        The entry zone: the draft readout, the keys, and the two commit actions.
        Guaranteed at least `--control-region-share` of the screen, and the
        keypad inside it is `flex-1`, so every pixel the fixed rows above and
        below do not need belongs to the keys. §18.2, §20.2

        The order below it used to hold one more thing — a line naming who took
        the last hand. It has gone, and the arithmetic is why: the fixed rows of
        this zone are already some 370 px on a 320 × 568 phone, and a 48 px tap
        target under them left a table of eight with roughly forty pixels a row
        for a face, a name and a figure. The same fact reaches the table three
        other ways — the cards burst over the roster as the hand turns over, the
        winner's own avatar dances for thirty seconds, and the whole hand is one
        tap away in history — so it is the row that could go. §14.1, §4.4, §4.5,
        §13.1
      */}
      <section aria-label="Score entry" className="control-region flex flex-col">
        <AmountKeypad />
      </section>

      {/*
        The hand boundary. A sibling of the surface rather than a child of the
        roster: it is `position: fixed` over the whole screen, it must not be
        inside anything that clips or scrolls, and it must survive the roster
        reflowing under it. Mounted only here, so it exists for exactly the screen
        that has hands to end — never over the lobby or the final scores. §4.4
      */}
      <RoundExplosion />

      <HostControls />
      <Overlays />
    </div>
  );
}
